// The device page in effect (device-dashboards M2, vision-api 0.19.0 WidgetPage.layout): the user's, the
// organisation's or the profile's page, edited bound to the unit and saved back with `$self` and its `i18n:` titles.
import type { CreavisionDashboardDocument } from "../../../documents/generated/dashboard";
import type { Widget } from "../../../widgets/types";
import { bindSelf, SELF } from "./profile";
import type { WidgetPage } from "./queries";

type DocWidget = CreavisionDashboardDocument["widgets"][number];

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);

/** `layout` when it is a device page with widgets; a server without the device page routes sends none. */
export function resolvedPage(p: WidgetPage | undefined): CreavisionDashboardDocument | undefined {
  const d = p?.layout;
  if (!isObject(d) || d.kind !== "device_page" || !Array.isArray(d.widgets)) return undefined;
  return d as unknown as CreavisionDashboardDocument;
}

/** A bound widget as a device-page widget again: the unit's id becomes `$self` (DDB-20). */
export function unbindSelf(w: Widget, device: string): DocWidget {
  const { bindings, source, ...rest } = w;
  return {
    ...(rest as Omit<DocWidget, "bindings" | "source">),
    ...(bindings ? { bindings: bindings.map((b) => (b.device === device ? { ...b, device: SELF } : { ...b })) } : {}),
    ...(source
      ? {
          source: {
            ...source,
            ...(source.devices ? { devices: source.devices.map((d) => (d === device ? SELF : d)) } : {}),
          },
        }
      : {}),
  } as DocWidget;
}

/**
 * The document to save: the edited widgets unbound, each keeping its original `i18n:` title unless the person
 * renamed it (BE-DP-12: profile texts go back as keys).
 */
export function toSave(
  original: CreavisionDashboardDocument,
  edited: Widget[],
  device: string,
  texts: Record<string, string>,
): CreavisionDashboardDocument {
  const before = new Map(original.widgets.map((w) => [w.id, w]));
  const widgets = edited.map((e) => {
    const w = unbindSelf(e, device);
    const key = before.get(e.id)?.title;
    if (typeof key !== "string" || !key.startsWith("i18n:")) return w;
    const shown = texts[key] || undefined;
    return e.title === shown ? { ...w, title: key } : w;
  });
  return { ...original, kind: "device_page", widgets };
}

/**
 * The keys a widget binds on this unit that its type no longer has (DDB-26): the widget shows "no longer available"
 * instead of drawing.
 */
export function missingKeys(w: Widget, device: string, p: WidgetPage): string[] {
  return (w.bindings ?? []).flatMap((b) => {
    if (b.device !== device) return [];
    if (b.datapoint && !(b.datapoint in p.points)) return [b.datapoint];
    const action = b.command ?? b.setting;
    return action && !(action in p.actions) ? [action] : [];
  });
}

/**
 * The values a saved widget shows on this unit that its type no longer lets this widget type show (DTC-22): the
 * widget still draws, and the editor says so, so the person can change it.
 */
export function notOffered(w: Widget, device: string, p: WidgetPage): string[] {
  return (w.bindings ?? []).flatMap((b) => {
    const widgets = b.device === device && b.datapoint ? p.points[b.datapoint]?.widgets : undefined;
    return b.datapoint && widgets && !widgets.includes(w.type) ? [b.datapoint] : [];
  });
}

/** The current default's new widgets (DDB-26), placed below the page one under the other, keeping their size. */
export function addNewWidgets(widgets: Widget[], fresh: unknown[], device: string, texts: Record<string, string>) {
  let bottom = Math.max(0, ...widgets.map((w) => w.position.y + w.position.h));
  const taken = new Set(widgets.map((w) => w.id));
  const out = [...widgets];
  for (const raw of fresh) {
    if (!isObject(raw) || typeof raw.id !== "string" || typeof raw.type !== "string" || taken.has(raw.id)) continue;
    const pos = isObject(raw.position) ? raw.position : {};
    const w = typeof pos.w === "number" ? Math.min(12, pos.w) : 6;
    const h = typeof pos.h === "number" ? pos.h : 2;
    const doc = { ...raw, position: { x: 0, y: bottom, w, h } } as unknown as DocWidget;
    out.push(bindSelf(doc, device, texts));
    taken.add(raw.id);
    bottom += h;
  }
  return out;
}
